import { useState, useEffect } from 'react';
import TemplateCard from './TemplatesManager/components/TemplateCard';
import SearchBar from '../shared/SearchBar';
import LoadingStates from '../shared/LoadingStates';
import CategoryTabs from './TemplatesManager/components/CategoryTabs';
import EditorPanel from './TemplatesManager/components/EditorPanel';
import { useTemplates } from './TemplatesManager/hooks/useTemplates';
import { useEditorPanel } from './TemplatesManager/hooks/useEditorPanel';

const TemplatesManager = ({ onApplyTemplate, authData, isAuthenticated, providerInfo }) => {
  const [activeCategory, setActiveCategory] = useState('provider');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  
  // Provider search states
  const [providerSearchId, setProviderSearchId] = useState('');
  const [providerSearchResults, setProviderSearchResults] = useState(null);
  const [providerSearchLoading, setProviderSearchLoading] = useState(false);
  const [providerSearchError, setProviderSearchError] = useState(null);
  
  // Provider dropdown states
  const [availableProviders, setAvailableProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersLoaded, setProvidersLoaded] = useState(false);

  const {
    templates, loading, error, refreshTemplates, createTemplate,
    updateTemplate, deleteTemplate, searchProviderTemplates, fetchHealthSystemProviders
  } = useTemplates(authData, activeCategory, providerInfo);

  const {
    isEditorOpen, editorMode, openEditor, closeEditor, editorTemplate
  } = useEditorPanel();

  // Filter templates based on search query
  const filteredTemplates = templates.filter(template => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return template.name.toLowerCase().includes(query) ||
           template.description?.toLowerCase().includes(query) ||
           template.content?.toLowerCase().includes(query);
  });

  // Handle template selection for preview
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  // Handle template application
  const handleApplyTemplate = (template, mode = 'full', targetSection = null) => {
    onApplyTemplate({ template, mode, targetSection });
  };
  // Handle template editing
  const handleEditTemplate = (template) => {
    openEditor('edit', template);
  };
  // Handle template creation
  const handleCreateTemplate = () => {
    openEditor('create');
  };
  // Handle template copy - opens Create Template editor with pre-populated data
  const handleCopyTemplate = (template) => {
    // Create a copy of the template data for the new template
    const templateDataForCopy = {
      name: `Copy of ${template.name}`,
      description: template.description || '',
      content: template.content || '',
      template_type: template.template_type || 'full',
      is_custom: true
    };
    
    // Open the editor in create mode with the pre-populated data
    openEditor('create', templateDataForCopy);
  };
  // Handle template deletion
  const handleDeleteTemplate = async (template) => {
    if (window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      try {
        await deleteTemplate(template.id);
        refreshTemplates();
        if (selectedTemplate?.id === template.id) {
          setSelectedTemplate(null);
        }
      } catch (error) {
        console.error('Failed to delete template:', error);
      }
    }
  };
  // Toggle search visibility
  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
    if (!isSearchVisible) {
      // Clear search when hiding
      setSearchQuery('');
    }
  };

  // Handle provider search
  const handleProviderSearch = async () => {
    if (!providerSearchId.trim()) {
      setProviderSearchError('Please select a provider');
      return;
    }

    setProviderSearchLoading(true);
    setProviderSearchError(null);
    
    try {
      const result = await searchProviderTemplates(providerSearchId.trim());
      setProviderSearchResults(result);
    } catch (error) {
      setProviderSearchError(error.message || 'Failed to search provider templates');
      setProviderSearchResults(null);
    } finally {
      setProviderSearchLoading(false);
    }
  };

  // Handle copying provider template - opens Create Template editor with pre-populated data
  const handleCopyProviderTemplate = (template) => {
    // Create a copy of the template data for the new template
    const templateDataForCopy = {
      name: `Copy of ${template.name}`,
      description: template.description || '',
      content: template.content || '',
      template_type: template.template_type || 'full',
      is_custom: true
    };
    
    // Open the editor in create mode with the pre-populated data
    openEditor('create', templateDataForCopy);
    
    // Clear the search results to focus on the editor
    setProviderSearchResults(null);
  };

  // Clear provider search
  const clearProviderSearch = () => {
    setProviderSearchId('');
    setProviderSearchResults(null);
    setProviderSearchError(null);
  };

  // Reset providers loaded state when switching away from healthcare tab
  useEffect(() => {
    if (activeCategory !== 'health_system') {
      setProvidersLoaded(false);
      setAvailableProviders([]);
      setProviderSearchError(null);
    }
  }, [activeCategory]);

  // Load providers when healthcare tab becomes active
  useEffect(() => {
    if (isAuthenticated && activeCategory === 'health_system' && !providersLoaded) {
      const loadProviders = async () => {
        setProvidersLoading(true);
        try {
          const result = await fetchHealthSystemProviders();
          // Transform API response to match frontend expectations
          const transformedProviders = (result.providers || []).map(provider => ({
            id: provider.id,
            name: `${provider.first_name} ${provider.last_name}`.trim(),
            provider_type: provider.provider_type,
            specialties: provider.specialties ? provider.specialties.map(s => s.name) : []
          }));
          setAvailableProviders(transformedProviders);
          setProvidersLoaded(true);
        } catch (error) {
          console.error('Failed to load providers:', error);
          setProviderSearchError('Failed to load providers: ' + error.message);
        } finally {
          setProvidersLoading(false);
        }
      };
      
      loadProviders();
    }
  }, [isAuthenticated, activeCategory, providersLoaded, fetchHealthSystemProviders]);
  return (
    <div className="templates-manager">
      {/* Sticky Header */}
      <div className="templates-header">
        <div className="templates-header-main">
          <h3 className="popup-title">Note Templates</h3>
          <div className="templates-header-actions">
            {/* Search Button */}
            <button 
              className="button secondary"
              onClick={toggleSearch}
              aria-label="Toggle search"
              title="Search templates"
            >
              <span className="button-icon">🔍</span>
              <span className="button-text">Search</span>
            </button>

            {/* Add New Template Button */}
            {isAuthenticated && activeCategory === 'provider' && !isEditorOpen && (
              <button 
                className="button primary"
                onClick={handleCreateTemplate}
                aria-label="Create new template"
                title="Create new template"
              >
                <span className="button-icon">+</span>
                <span className="button-text">New</span>
              </button>
            )}
          </div>
        </div>

        {/* Collapsible Search Bar */}
        {isSearchVisible && (
          <div className="templates-search-container">
            <SearchBar 
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search templates..."
            />
          </div>
        )}
      </div>

      {/* Category Tabs */}
      <CategoryTabs
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        isAuthenticated={isAuthenticated}
      />

      {/* Provider Search Section (Healthcare Tab Only) */}
      {isAuthenticated && activeCategory === 'health_system' && (
        <div className="provider-search-section" style={{ padding: '16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
            Your Peers' Templates
          </h4>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              {providersLoading ? (
                <div style={{ padding: '8px', color: '#666', fontSize: '14px' }}>
                  Loading providers...
                </div>
              ) : availableProviders.length > 0 ? (
                <select
                  value={providerSearchId}
                  onChange={(e) => setProviderSearchId(e.target.value)}
                  className="search-input"
                  style={{ width: '100%', padding: '8px' }}
                >
                  <option value="">Select a provider...</option>
                  {availableProviders.map(provider => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name} {provider.provider_type && `(${provider.provider_type})`}
                      {provider.specialties?.length > 0 && ` - ${provider.specialties.join(', ')}`}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ padding: '8px', color: '#666', fontSize: '14px' }}>
                  No other providers found in your health system
                </div>
              )}
            </div>
            <button 
              className="button primary" 
              onClick={handleProviderSearch}
              disabled={providerSearchLoading}
            >
              {providerSearchLoading ? 'Searching...' : 'Search'}
            </button>
            {(providerSearchResults || providerSearchError) && (
              <button 
                className="button secondary" 
                onClick={clearProviderSearch}
              >
                Clear
              </button>
            )}
          </div>
          
          {providerSearchError && (
            <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33' }}>
              {providerSearchError}
            </div>
          )}
          
          {providerSearchResults && (
            <div style={{ marginTop: '12px' }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: '#666' }}>
                Templates from {providerSearchResults.provider_templates?.provider?.name}
              </h5>
              {providerSearchResults.provider_templates?.templates?.length > 0 ? (
                <div className="templates-grid" style={{ gap: '12px' }}>
                  {providerSearchResults.provider_templates.templates.map(template => (
                    <div key={template.id} className="template-item" style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <h6 style={{ margin: '0', fontSize: '14px', fontWeight: '600' }}>{template.name}</h6>
                        <button 
                          className="button primary"
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                          onClick={() => handleCopyProviderTemplate(template)}
                        >
                          Copy
                        </button>
                      </div>
                      {template.description && (
                        <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>{template.description}</p>
                      )}
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        Type: {template.template_type === 'full' ? 'Full Note' : 'Section'} • 
                        Provider: {template.provider_info?.first_name} {template.provider_info?.last_name}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666', fontSize: '14px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '6px' }}>
                  No templates found for this provider
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className="templates-content">
        {isAuthenticated ? (
          <>
        {loading ? (
          <LoadingStates message="Loading templates..." subMessage="Please wait while we fetch your templates" />
        ) : error ? (
          <div className="error-state">
            <div className="ankr-error-icon">⚠️</div>
            <p>Unable to load templates</p>
            <p className="small mb-3">{error}</p>
            <button onClick={refreshTemplates} className="button secondary">
              Try Again
            </button>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <div className="ankr-empty-icon">📝</div>
            <p>No templates found</p>
            <p className="small mb-3">
              {activeCategory === 'provider' 
                ? 'Create custom templates to speed up your documentation process.'
                : 'Browse available templates from your organization.'
              }
            </p>
            {activeCategory === 'provider' && (
              <button onClick={handleCreateTemplate} className="button primary">
                Create Template
              </button>
            )}
          </div>
        ) : (
          <div className="templates-grid">
            {filteredTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                category={activeCategory}
                isSelected={selectedTemplate?.id === template.id}
                onSelect={handleTemplateSelect}
                onApply={handleApplyTemplate}
                onEdit={handleEditTemplate}
                onCopy={handleCopyTemplate}
                onDelete={handleDeleteTemplate}
              />
            ))}
          </div>
        )}

          </>
        ) : (
          <div className="empty-state">
            <div className="ankr-empty-icon">🔐</div>
            <p>Sign in to access templates</p>
            <small>You can view and create personal and organization templates.</small>
          </div>
        )}
      </div>

      {/* Slide-in Editor Panel */}
      {isAuthenticated && (
        <EditorPanel
          isOpen={isEditorOpen}
          mode={editorMode}
          template={editorTemplate}
          providerInfo={providerInfo}
          authData={authData}
          onClose={closeEditor}
          onSave={async (templateData) => {
            try {
              if (editorMode === 'create') {
                await createTemplate(templateData);
              } else {
                await updateTemplate(editorTemplate.id, templateData);
              }
              closeEditor();
              refreshTemplates();
            } catch (error) {
              console.error('Failed to save template:', error);
              throw error;
            }
          }}
        />
      )}
    </div>
  );
};

export default TemplatesManager;